/**
 * AssistantPage (Workspace)
 *
 * Main workspace with additive retrieval insights:
 * - Existing canvas + chat workflows remain intact.
 * - Desktop adds optional retrieval insights dock (graph/context/plan).
 * - Mobile adds a dedicated Insights tab.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGraphStats } from '../../api/hooks/knowledge.hook';
import { WorkspaceCanvas } from '../../components/Workspace/canvas';
import { useWorkspace } from '../../components/Workspace/artifacts';
import { InsightsDock } from '../../components/Workspace/retrieval';
import { ChatSidebar } from '../../components/Workspace/sidebar';
import { useRetrievalExperience } from '../../hooks/useRetrievalExperience';

type MobileView = 'canvas' | 'chat' | 'insights';

const AssistantPage: React.FC = () => {
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(false);
  const [showInsights, setShowInsights] = useState(true);
  const [searchParams] = useSearchParams();
  const { openArtifacts } = useWorkspace();
  const { data: graphStats } = useGraphStats();
  const { state: retrievalState } = useRetrievalExperience();

  const [mobileView, setMobileView] = useState<MobileView>(() => {
    const artifactId = new URLSearchParams(window.location.search).get('artifact');
    const isMobile = window.innerWidth < 1024;
    if (artifactId && isMobile) {
      return 'canvas';
    }
    return 'chat';
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileScreen(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const artifactId = searchParams.get('artifact');
    const hasOpenArtifacts = openArtifacts && openArtifacts.length > 0;
    const isMobile = window.innerWidth < 1024;

    if ((artifactId || hasOpenArtifacts) && isMobile) {
      setMobileView('canvas');
    }
  }, [searchParams, openArtifacts]);

  useEffect(() => {
    const handleOpenInsights = () => {
      setShowInsights(true);
      if (window.innerWidth < 1024) {
        setMobileView('insights');
      }
    };

    window.addEventListener('curious:insights-open', handleOpenInsights);
    return () => {
      window.removeEventListener('curious:insights-open', handleOpenInsights);
    };
  }, []);

  const streamStatus = retrievalState.streamStatus;
  const streamStatusLabel =
    streamStatus === 'streaming'
      ? 'Streaming'
      : streamStatus === 'completed'
        ? 'Verified'
        : streamStatus === 'error'
          ? 'Issue'
          : 'Idle';

  const streamStatusClasses =
    streamStatus === 'streaming'
      ? 'bg-blue-100 text-blue-700 border-blue-200'
      : streamStatus === 'completed'
        ? 'bg-green-100 text-green-700 border-green-200'
        : streamStatus === 'error'
          ? 'bg-red-100 text-red-700 border-red-200'
          : 'bg-neutral-100 text-neutral-600 border-neutral-200';

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="lg:hidden flex items-center border-b border-neutral-200 bg-white shrink-0">
        <button
          type="button"
          onClick={() => setMobileView('canvas')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            mobileView === 'canvas'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
          }`}
        >
          <span>Workspace</span>
        </button>
        <button
          type="button"
          onClick={() => setMobileView('chat')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            mobileView === 'chat'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
          }`}
        >
          <span>Assistant</span>
        </button>
        <button
          type="button"
          onClick={() => setMobileView('insights')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            mobileView === 'insights'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
              : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
          }`}
        >
          <span>Insights</span>
        </button>
      </div>

      <div className="hidden lg:flex items-center justify-between gap-3 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
        <div className="flex items-center gap-3 text-xs text-neutral-600">
          <span>
            Graph <strong className="text-neutral-800">{graphStats?.total_nodes ?? 0}</strong> nodes
          </span>
          <span>
            <strong className="text-neutral-800">{graphStats?.total_relationships ?? 0}</strong>{' '}
            edges
          </span>
          <span>
            <strong className="text-neutral-800">{graphStats?.documents_total ?? 0}</strong> docs
            indexed
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${streamStatusClasses}`}
            title={retrievalState.lastError || undefined}
          >
            {streamStatusLabel}
          </span>
          <button
            type="button"
            onClick={() => setShowInsights((prev) => !prev)}
            className="rounded border border-neutral-200 bg-white px-3 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
          >
            {showInsights ? 'Hide Insights' : 'Show Insights'}
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden bg-white">
        <div
          className={`flex-1 min-w-0 min-h-0 overflow-hidden ${
            mobileView === 'canvas' ? 'flex' : 'hidden'
          } lg:flex`}
        >
          <WorkspaceCanvas className="h-full w-full rounded-tl-xl border-t border-l border-neutral-200" />
        </div>

        <div className={`${mobileView === 'chat' ? 'flex w-full' : 'hidden'} lg:flex lg:w-auto`}>
          <ChatSidebar
            isCollapsed={isChatCollapsed}
            onToggle={() => setIsChatCollapsed((prev) => !prev)}
            isMobile={isMobileScreen && mobileView === 'chat'}
          />
        </div>

        <div
          className={`${
            mobileView === 'insights' ? 'flex w-full' : 'hidden'
          } lg:hidden flex-col overflow-hidden bg-white`}
        >
          <InsightsDock />
        </div>

        {showInsights ? (
          <div className="hidden lg:flex lg:min-h-0 lg:w-[32rem]">
            <InsightsDock />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AssistantPage;
